import styled from '@emotion/styled';
import { captureException } from '@sentry/browser';
import { Input } from 'antd';
import React, { useState } from 'react';
import ReactGA from 'react-ga';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';

import { userService } from 'src/config/rootService';
import { BrandWrapper } from 'src/modules/onboarding/components/BrandWrapper';
import { Button } from 'src/shared/components/Button';
import { color, font } from 'src/shared/styles/variables';

const Wrapper = styled.div({
    maxWidth: 570,
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
});

const Title = styled.h3({
    textAlign: 'center',
    marginTop: 20,
    marginBottom: 20,
    color: color.pickledBluewood,
    ...font.black(font.size.header),
});

const Description = styled.h5({
    textAlign: 'center',
    color: color.steelGray,
    lineHeight: '28px',
    marginBottom: 25,
    maxWidth: 400,
    ...font.book(font.size.mediumExtended),
});

const TextArea = styled(Input.TextArea)({
    '&&': {
        maxWidth: 510,
        marginTop: 16,
        padding: 11,
        border: `2px solid ${color.linkWaterLight}`,
        color: color.blueBayoux,
        ...font.medium(font.size.base),
    },
});

const ButtonWrapper = styled.div({
    maxWidth: 510,
    marginTop: 32,
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
});

export const ReTriageRequestCareAdvisorPage: React.FC<RouteComponentProps> = ({ history }) => {
    const { t } = useTranslation();
    const [text, setText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        setSubmitting(true);

        ReactGA.event({
            category: 'Re-triage flow',
            action: 'Submitted care advisor request',
            label: 'retriage_submit_ca_clicked',
        });

        try {
            await userService.submitHelpDeskRequest(text, ['Request care advisor re-triage flow']);
        } catch (err) {
            captureException(err);
        } finally {
            setSubmitting(false);
            history.push('/re-triage-thank-you');
        }
    };

    return (
        <BrandWrapper>
            <Wrapper>
                <Title>{t('retriage:request_care_page.title')}</Title>
                <Description>{t('retriage:request_care_page.description')}</Description>

                <TextArea
                    rows={7}
                    placeholder={t('retriage:request_care_page.placeholder')}
                    value={text}
                    onChange={({ target }) => setText(target.value)}
                />

                <ButtonWrapper>
                    <Button
                        onClick={() => history.goBack()}
                        type="white"
                        widthPercentage={49}
                        withShadow
                        noHorizontalPadding
                    >
                        {t('common.back')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        loading={submitting}
                        widthPercentage={49}
                        noHorizontalPadding
                        withShadow
                    >
                        {t('retriage:request_care_page.submit')}
                    </Button>
                </ButtonWrapper>
            </Wrapper>
        </BrandWrapper>
    );
};
